import { useDirectSaleDetail } from '@/core/domain/sales/DirectSale/DirectSale.hooks';
import { DirectSale } from '@/core/domain/sales/DirectSale/DirectSale.types';
import {
  useStoneOrderCancel,
  useStoneOrderHasOpenMutate,
} from '@/core/domain/stone/orders/orders.hooks';
import {
  StoneOrderHasOpenOrderResponse,
  StoneOrderOriginType,
  StoneOrderStatusType,
} from '@/core/domain/stone/orders/orders.types';
import { useWorkOrderMutation } from '@/core/domain/workorder/WorkOrders/WorkOrders.hooks';
import { WorkOrder } from '@/core/domain/workorder/WorkOrders/WorkOrders.types';
import { Modal, message } from 'antd';
import { useCallback, useEffect, useState } from 'react';

interface Props {
  isStoneEnabled: boolean;
  deviceId?: number;
  onFinish?: (type: 'order' | 'cancelation') => void;
}

interface Params<T> {
  data?: StoneOrderHasOpenOrderResponse;
  origin?: T;
}

export interface StoneHasOrderInfoHookReturn<T = StoneOriginOrderTypeHook> {
  isLoading: boolean;
  resultData?: Params<T>;
  cancelOrder: VoidFunction;
}

type StoneOriginOrderTypeHook =
  | (WorkOrder & { originType: StoneOrderOriginType.WORK_ORDER })
  | (DirectSale & { originType: StoneOrderOriginType.SALE });

export default function useStoneHasOrderInfo<T = StoneOriginOrderTypeHook>(
  props: Props,
): StoneHasOrderInfoHookReturn<T> {
  const stoneHasOrderOpen = useStoneOrderHasOpenMutate();
  const stoneOrderCancel = useStoneOrderCancel();
  const workorderDetail = useWorkOrderMutation();
  const directSaleDatail = useDirectSaleDetail();

  const [resultData, setResultData] = useState<Params<T>>();

  const isLoading =
    stoneHasOrderOpen.isLoading ||
    workorderDetail.isLoading ||
    directSaleDatail.isLoading;

  useEffect(() => {
    if (!props.isStoneEnabled) {
      setResultData(undefined);
      stoneHasOrderOpen.reset();
    }
  }, [props.isStoneEnabled]);

  const cancelOrder = useCallback(() => {
    Modal.confirm({
      width: 500,
      title: 'Cancelamento de pedido',
      content: `Deseja cancelar o pedido de financeiro com a Stone ${
        stoneHasOrderOpen.data?.originType === StoneOrderOriginType.SALE
          ? 'da venda direta'
          : 'do orçamento'
      }?`,
      cancelText: 'fechar',
      okText: 'Cancelar pedido',
      okType: 'danger',
      onOk: async () => {
        if (stoneHasOrderOpen.data?.orderId && !isLoading) {
          await stoneOrderCancel.mutateAsync({
            orderId: stoneHasOrderOpen.data?.orderId,
          });
          const result = await stoneHasOrderOpen.mutateAsync(props.deviceId);
          setResultData((prev) => ({
            ...prev,
            data: result,
          }));
        }
      },
    });
  }, [stoneHasOrderOpen.data, isLoading, props]);

  useEffect(() => {
    (async () => {
      if (
        props.isStoneEnabled &&
        props.deviceId &&
        !stoneHasOrderOpen.data &&
        !resultData?.origin &&
        !isLoading
      ) {
        await getInfo();
      }
    })();
  }, [props, stoneHasOrderOpen.data, resultData?.origin, isLoading]);

  useEffect(() => {
    if (
      stoneHasOrderOpen.data &&
      !stoneHasOrderOpen.data?.hasOpenOrder &&
      resultData?.data?.hasOpenOrder &&
      props.isStoneEnabled &&
      !isLoading
    ) {
      if (
        resultData.data.orderStatus ===
        StoneOrderStatusType.processing_cancellation
      ) {
        if (props.onFinish) {
          props.onFinish('cancelation');
        }
        message.info('O processamento de cancelamento foi finalizado!');
      } else {
        if (props.onFinish) {
          props.onFinish('order');
        }
        message.info('O processamento do pedido com a Stone foi finalizado!');
      }
    }
  }, [stoneHasOrderOpen.data, resultData?.data, props, isLoading]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (
      resultData?.data &&
      resultData?.data?.hasOpenOrder &&
      (resultData?.data.orderStatus ===
        StoneOrderStatusType.processing_cancellation ||
        resultData?.data.orderStatus ===
          StoneOrderStatusType.processing_order) &&
      !isLoading &&
      props.deviceId &&
      props.isStoneEnabled
    ) {
      timeout = setTimeout(async () => {
        await getInfo();
      }, 10000);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [resultData, props, isLoading]);

  const getInfo = useCallback(async () => {
    if (props.deviceId) {
      const result = await stoneHasOrderOpen.mutateAsync(props.deviceId);

      if (result?.originType === StoneOrderOriginType.WORK_ORDER) {
        let workorderData = workorderDetail.data;

        if (result.originId !== workorderDetail.data?.id) {
          workorderData = await workorderDetail.mutateAsync(result?.originId);
        }

        if (workorderData) {
          setResultData({
            data: result,

            origin: {
              ...workorderData,
              originType: StoneOrderOriginType.WORK_ORDER,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any,
          });
        }
      } else if (result?.originType === StoneOrderOriginType.SALE) {
        let directSaleData = directSaleDatail.data;

        if (result.originId !== directSaleData?.id) {
          directSaleData = await directSaleDatail.mutateAsync(result?.originId);
        }

        if (directSaleData) {
          setResultData({
            data: result,
            origin: {
              ...directSaleData,
              originType: StoneOrderOriginType.SALE,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any,
          });
        }
      } else {
        setResultData({
          data: result,
          origin: undefined,
        });
      }
    }
  }, [props, directSaleDatail.data, workorderDetail.data]);

  return {
    isLoading,
    resultData,
    cancelOrder,
  };
}
